import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Star, AlertTriangle, X, MessageCircle, Sparkles, ShieldX } from 'lucide-react';
import { clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';

// Utility function
const cn = (...inputs) => twMerge(clsx(inputs));

// Lista ampliada de palabras ofensivas a filtrar
const offensiveWords = [
  // Insultos generales
  'idiota', 'estúpido', 'imbécil', 'pendejo', 'baboso', 
  'gilipollas', 'subnormal', 'retrasado', 'tarado', 
  'boludo', 'pelotudo', 'huevón', 'huevon', 'gil', 'tonto', 'bruto',
  'inútil', 'inutil', 'cabrón', 'cabron', 'jodido', 'conchatumare', 
  'conchatumadre', 'cabro', 'kbron', 'kbraso', 'cabraso', 'veneco', 'veneca', 'veneko', 'veneka',

  // Insultos más fuertes
  'hijo de puta', 'hdp', 'mierda', 'puto', 'puta', 'concha', 
  'coño', 'zorra', 'culiao', 'maraca', 'ctm', 'cabrona',
  'malparido', 'marica', 'chinga', 'joto', 'joder',

  // Variaciones comunes (para capturar más casos)
  'mierd', 'mierdasss', 'mierdaa', 'mrd',
  'put', 'putoo', 'putaa', 'pt',
  'cagad', 'cagon', 'cagar', 'cag', 'mierfa', 'puto',

  // Insultos en diferentes contextos
  'mierda de', 'puta madre', 'hijo de',

  // Amenazas y violencia
  'matar', 'morir', 'muerte', 'asesinar', 'golpear', 'pegar',
  'romper', 'destruir', 'quemar', 'lastimar', 'dañar',

  // Términos discriminatorios
  'negro de', 'indio de', 'cholo', 'serrano',
  
  // Variaciones con números y símbolos
  'p3nd3jo', 'm13rd4', 'p*t*', 'm*****'
];

// Función mejorada para detectar palabras ofensivas
const containsOffensiveWords = (text) => {
  if (!text) return false;
  
  // Normalizar el texto: convertir a minúsculas y remover caracteres especiales
  let normalizedText = text.toLowerCase()
    .replace(/[^\w\s]/g, ' ') // Reemplazar caracteres especiales con espacios
    .replace(/\s+/g, ' ') // Reemplazar múltiples espacios con uno solo
    .trim();
  
  // También crear una versión sin espacios para detectar palabras unidas
  let noSpacesText = normalizedText.replace(/\s+/g, '');
  
  // Verificar frases completas primero
  const phrases = offensiveWords.filter(word => word.includes(' '));
  for (const phrase of phrases) {
    if (normalizedText.includes(phrase)) {
      return true;
    }
  }
  
  // Verificar palabras individuales con límites de palabra
  const singleWords = offensiveWords.filter(word => !word.includes(' '));
  for (const word of singleWords) {
    // Crear regex que busque la palabra con límites
    const wordRegex = new RegExp(`\\b${word.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`, 'i');
    if (wordRegex.test(normalizedText)) {
      return true;
    }
    
    // También verificar en texto sin espacios (para palabras unidas intencionalmente)
    if (noSpacesText.includes(word)) {
      return true;
    }
  }
  
  // Verificar patrones de evasión comunes
  const evasionPatterns = [
    /p[\*\.\-_]?u[\*\.\-_]?t[\*\.\-_]?[oa]/i, // p*t*, p.u.t.a, etc.
    /m[\*\.\-_]?i[\*\.\-_]?e[\*\.\-_]?r[\*\.\-_]?d[\*\.\-_]?a/i, // m*erda, mi.er.da, etc.
    /c[\*\.\-_]?a[\*\.\-_]?b[\*\.\-_]?r[\*\.\-_]?[oó][\*\.\-_]?n/i, // c*brón, etc.
    /h[\*\.\-_]?d[\*\.\-_]?p/i, // h*d*p, etc.
  ];
  
  for (const pattern of evasionPatterns) {
    if (pattern.test(text)) {
      return true;
    }
  }
  
  return false;
};

// Función para obtener palabras detectadas (para debugging o mostrar al usuario)
const getDetectedWords = (text) => {
  if (!text) return [];
  
  const detected = [];
  let normalizedText = text.toLowerCase()
    .replace(/[^\w\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  
  // Verificar frases
  const phrases = offensiveWords.filter(word => word.includes(' '));
  for (const phrase of phrases) {
    if (normalizedText.includes(phrase)) {
      detected.push(phrase);
    }
  }
  
  // Verificar palabras individuales
  const singleWords = offensiveWords.filter(word => !word.includes(' '));
  for (const word of singleWords) {
    const wordRegex = new RegExp(`\\b${word.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`, 'i');
    if (wordRegex.test(normalizedText)) {
      detected.push(word);
    }
  }
  
  return [...new Set(detected)]; // Remover duplicados
};

// Dialog Components with Dark Mode Support
const Dialog = ({ open, onOpenChange, children }) => {
  useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [open]);

  if (!open) return null;

  return (
    <motion.div 
      className="fixed inset-0 z-50 flex items-center justify-center"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.2 }}
    >
      <motion.div 
        className="fixed inset-0 bg-black/60 backdrop-blur-sm" 
        onClick={() => onOpenChange(false)}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
      />
      <div className="relative z-50 w-full max-w-lg mx-4">
        {children}
      </div>
    </motion.div>
  );
};

const DialogContent = ({ className, children, onClose, ...props }) => (
  <motion.div
    className={cn(
      "relative backdrop-blur-xl bg-white/95 dark:bg-gray-800/95 rounded-2xl shadow-2xl dark:shadow-black/50 w-full p-6 border border-white/20 dark:border-gray-600 overflow-hidden",
      className
    )}
    initial={{ opacity: 0, scale: 0.95, y: 20 }}
    animate={{ opacity: 1, scale: 1, y: 0 }}
    exit={{ opacity: 0, scale: 0.95, y: 20 }}
    transition={{ duration: 0.3, ease: [0.4, 0, 0.2, 1] }}
    {...props}
  >
    {/* Gradient overlay */}
    <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-indigo-500/5 via-purple-500/5 to-pink-500/5 dark:from-indigo-400/10 dark:via-purple-400/10 dark:to-pink-400/10" />
    
    {/* Close button */}
    <motion.button
      onClick={onClose}
      className="absolute top-4 right-4 p-2 rounded-full bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600 hover:text-gray-700 dark:hover:text-gray-200 transition-all duration-200 z-10"
      whileHover={{ scale: 1.1 }}
      whileTap={{ scale: 0.95 }}
    >
      <X className="w-4 h-4" />
    </motion.button>
    
    <div className="relative z-10">
      {children}
    </div>
  </motion.div>
);

const DialogHeader = ({ className, ...props }) => (
  <div
    className={cn("flex flex-col space-y-3 text-center mb-6", className)}
    {...props}
  />
);

const DialogTitle = ({ className, children, ...props }) => (
  <div className="flex items-center justify-center gap-3 mb-2">
    <div className="p-2 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl">
      <Star className="w-5 h-5 text-white" />
    </div>
    <h3
      className={cn(
        "text-2xl font-bold text-gray-800 dark:text-gray-200",
        className
      )}
      {...props}
    >
      {children}
    </h3>
  </div>
);

// Enhanced Button Component
const Button = React.forwardRef(
  ({ className, variant = "default", size = "default", disabled, children, ...props }, ref) => {
    const baseStyles = "inline-flex items-center justify-center rounded-xl font-medium transition-all duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-gray-800 disabled:pointer-events-none disabled:opacity-50";
    
    const variants = {
      default: "bg-gray-900 dark:bg-gray-700 text-gray-50 shadow-lg hover:bg-gray-800 dark:hover:bg-gray-600 hover:shadow-xl",
      primary: "bg-gradient-to-r from-indigo-600 to-purple-600 text-white shadow-lg hover:shadow-xl dark:shadow-xl dark:hover:shadow-2xl transform hover:scale-105 hover:-translate-y-0.5",
      destructive: "bg-gradient-to-r from-red-500 to-red-600 text-white shadow-lg hover:shadow-xl transform hover:scale-105",
      outline: "border-2 border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 shadow-sm hover:bg-gray-50 dark:hover:bg-gray-700 hover:border-gray-300 dark:hover:border-gray-500",
      secondary: "bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-gray-200 shadow-sm hover:bg-gray-200 dark:hover:bg-gray-600",
      ghost: "hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-900 dark:text-gray-200",
    };

    const sizes = {
      default: "h-11 px-6 py-3 text-sm",
      sm: "h-9 px-4 py-2 text-xs",
      lg: "h-12 px-8 py-3 text-base",
      icon: "h-10 w-10",
    };

    return (
      <motion.button
        className={cn(
          baseStyles,
          variants[variant],
          sizes[size],
          className
        )}
        ref={ref}
        disabled={disabled}
        whileHover={!disabled ? { scale: variant === "primary" ? 1.05 : 1.02 } : {}}
        whileTap={!disabled ? { scale: 0.98 } : {}}
        {...props}
      >
        {children}
      </motion.button>
    );
  }
);
Button.displayName = "Button";

// Enhanced Textarea Component
const Textarea = React.forwardRef(
  ({ className, error, ...props }, ref) => {
    return (
      <motion.div
        className="relative"
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      >
        <textarea
          className={cn(
            "flex min-h-[120px] w-full rounded-xl border-2 border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 px-4 py-3 text-sm text-gray-900 dark:text-gray-100 shadow-sm placeholder:text-gray-500 dark:placeholder:text-gray-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:border-transparent disabled:cursor-not-allowed disabled:opacity-50 transition-all duration-300 resize-none",
            error && "border-red-500 dark:border-red-400 focus-visible:ring-red-500",
            className
          )}
          ref={ref}
          {...props}
        />
      </motion.div>
    );
  }
);
Textarea.displayName = "Textarea";

// Star Rating Component
const StarRating = ({ rating, onRatingChange }) => {
  const [hoverRating, setHoverRating] = useState(0);

  return (
    <motion.div 
      className="flex justify-center gap-2 py-4"
      initial={{ opacity: 0, scale: 0.8 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.5, delay: 0.2 }}
    >
      {[1, 2, 3, 4, 5].map((value) => (
        <motion.button
          key={value}
          type="button"
          className="focus:outline-none"
          onMouseEnter={() => setHoverRating(value)}
          onMouseLeave={() => setHoverRating(0)}
          onClick={() => onRatingChange(value)}
          whileHover={{ scale: 1.2, rotate: 5 }}
          whileTap={{ scale: 1.1 }}
          transition={{ type: "spring", stiffness: 400, damping: 10 }}
        >
          <Star
            className={`w-10 h-10 transition-all duration-200 ${
              value <= (hoverRating || rating) 
                ? 'fill-yellow-400 text-yellow-400 drop-shadow-lg' 
                : 'text-gray-300 dark:text-gray-600 hover:text-yellow-300'
            }`}
          />
        </motion.button>
      ))}
    </motion.div>
  );
};

// Rating descriptors
const getRatingDescription = (rating) => {
  const descriptions = {
    1: { text: "Muy malo", color: "text-red-500 dark:text-red-400" },
    2: { text: "Malo", color: "text-orange-500 dark:text-orange-400" },
    3: { text: "Regular", color: "text-yellow-500 dark:text-yellow-400" },
    4: { text: "Bueno", color: "text-green-500 dark:text-green-400" },
    5: { text: "Excelente", color: "text-emerald-500 dark:text-emerald-400" }
  };
  
  return descriptions[rating] || { text: "", color: "" };
};

// Warning Component - Mejorado
const OffensiveContentWarning = ({ detectedWords = [] }) => (
  <motion.div 
    className="flex items-start gap-3 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl"
    initial={{ opacity: 0, height: 0 }}
    animate={{ opacity: 1, height: "auto" }}
    exit={{ opacity: 0, height: 0 }}
    transition={{ duration: 0.3 }}
  >
    <div className="p-1 bg-red-500/20 rounded-full">
      <ShieldX className="w-4 h-4 text-red-600 dark:text-red-400 flex-shrink-0" />
    </div>
    <div className="flex-1">
      <p className="text-sm font-medium text-red-800 dark:text-red-200 mb-1">
        ⚠️ Contenido inapropiado detectado
      </p>
      <p className="text-sm text-red-700 dark:text-red-300 mb-2">
        Tu comentario contiene lenguaje ofensivo o inapropiado. 
        No podrás enviar la reseña hasta que modifiques el contenido.
      </p>
      <p className="text-xs text-red-600 dark:text-red-400">
        Por favor, mantén un lenguaje respetuoso para crear un ambiente positivo para todos.
      </p>
    </div>
  </motion.div>
);

// Main Rating Modal Component
const RatingModal = ({ isOpen, onClose, onSubmit, userType, existingReview = null }) => {
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState('');
  const [error, setError] = useState('');
  const [hasOffensiveWords, setHasOffensiveWords] = useState(false);
  const [detectedWords, setDetectedWords] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [safetyTimer, setSafetyTimer] = useState(null);

  useEffect(() => {
    if (existingReview) {
      setRating(existingReview.rating);
      setComment(existingReview.comentario || '');
    } else {
      setRating(0);
      setComment('');
    }
    setError('');
    setHasOffensiveWords(false);
    setDetectedWords([]);
    setIsSubmitting(false);
    
    // Limpiar cualquier timer existente
    if (safetyTimer) {
      clearTimeout(safetyTimer);
      setSafetyTimer(null);
    }
  }, [existingReview, isOpen]);

  // Función para forzar recarga y cierre
  const forceReloadAndClose = () => {
    onClose();
    window.location.reload();
  };

  const handleSubmit = () => {
    if (isSubmitting) return;
    
    // Validación básica
    if (!rating) {
      setError('Por favor selecciona una calificación');
      return;
    }

    if (!comment.trim()) {
      setError('Por favor escribe un comentario');
      return;
    }

    if (comment.length > 500) {
      setError('El comentario no debe exceder los 500 caracteres');
      return;
    }

    // ✅ VALIDACIÓN MEJORADA DE CONTENIDO OFENSIVO
    if (hasOffensiveWords) {
      setError('No se puede enviar la reseña con contenido ofensivo. Por favor modifica tu comentario.');
      return;
    }

    // Verificación adicional por si acaso
    if (containsOffensiveWords(comment)) {
      setError('Se detectó contenido inapropiado. Por favor revisa tu comentario.');
      setHasOffensiveWords(true);
      return;
    }

    setIsSubmitting(true);
    setError('');
    
    // Configurar un timer de seguridad
    const timer = setTimeout(() => {
      console.log('Safety timer activated - forcing reload');
      forceReloadAndClose();
    }, 1000);
    
    setSafetyTimer(timer);
    
    try {
      const result = onSubmit({ rating, comment });
      
      if (result && typeof result.then === 'function') {
        result
          .then(() => {
            clearTimeout(timer);
            forceReloadAndClose();
          })
          .catch(err => {
            clearTimeout(timer);
            console.error('Error al enviar reseña:', err);
            setError('Ocurrió un error al enviar tu reseña. Por favor intenta nuevamente.');
            setIsSubmitting(false);
          });
      } else {
        setTimeout(() => {
          clearTimeout(timer);
          forceReloadAndClose();
        }, 500);
      }
    } catch (err) {
      clearTimeout(timer);
      console.error('Error al enviar reseña:', err);
      setError('Ocurrió un error al enviar tu reseña. Por favor intenta nuevamente.');
      setIsSubmitting(false);
    }
  };

  const handleCommentChange = (e) => {
    const newComment = e.target.value;
    
    if (newComment.length <= 500) {
      setComment(newComment);
      
      // Verificar contenido ofensivo en tiempo real
      const hasOffensive = containsOffensiveWords(newComment);
      setHasOffensiveWords(hasOffensive);
      
      if (hasOffensive) {
        const detected = getDetectedWords(newComment);
        setDetectedWords(detected);
      } else {
        setDetectedWords([]);
      }
      
      // Limpiar errores relacionados con longitud
      if (error.includes('caracteres')) {
        setError('');
      }
      
      // Limpiar error de contenido ofensivo si ya no hay
      if (!hasOffensive && error.includes('ofensivo')) {
        setError('');
      }
    }
  };

  // Limpiar el timer al desmontar el componente
  useEffect(() => {
    return () => {
      if (safetyTimer) {
        clearTimeout(safetyTimer);
      }
    };
  }, [safetyTimer]);

  const ratingDescription = getRatingDescription(rating);

  // ✅ VALIDAR SI EL BOTÓN DEBE ESTAR DESHABILITADO
  const isSubmitDisabled = !rating || !comment.trim() || hasOffensiveWords || isSubmitting;

  return (
    <AnimatePresence>
      {isOpen && (
        <Dialog open={isOpen} onOpenChange={onClose}>
          <DialogContent onClose={onClose}>
            <DialogHeader>
              <DialogTitle>
                {existingReview ? 'Actualizar calificación' : `Calificar ${userType}`}
              </DialogTitle>
              <motion.p 
                className="text-gray-600 dark:text-gray-400 text-sm"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.3 }}
              >
                Tu opinión es muy importante para nosotros
              </motion.p>
            </DialogHeader>

            <div className="space-y-6">
              {/* Star Rating */}
              <div className="text-center">
                <StarRating rating={rating} onRatingChange={setRating} />
                
                <AnimatePresence>
                  {rating > 0 && (
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      className="mt-2"
                    >
                      <span className={`text-lg font-semibold ${ratingDescription.color}`}>
                        {ratingDescription.text}
                      </span>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              {/* Comment Section */}
              <motion.div 
                className="space-y-3"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
              >
                <div className="flex items-center gap-2 mb-2">
                  <MessageCircle className="w-4 h-4 text-indigo-500 dark:text-indigo-400" />
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Comparte tu experiencia
                  </label>
                </div>
                
                <Textarea
                  placeholder="Cuéntanos sobre tu experiencia..."
                  value={comment}
                  onChange={handleCommentChange}
                  error={hasOffensiveWords}
                  maxLength={500}
                />
                
                <div className="flex justify-between items-center text-sm">
                  <span className={`transition-colors ${
                    comment.length > 450 
                      ? 'text-red-500 dark:text-red-400' 
                      : 'text-gray-500 dark:text-gray-400'
                  }`}>
                    {comment.length}/500 caracteres
                  </span>
                  
                  {error && (
                    <motion.span 
                      className="text-red-500 dark:text-red-400 font-medium"
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                    >
                      {error}
                    </motion.span>
                  )}
                </div>

                {/* Offensive Content Warning - Mejorado */}
                <AnimatePresence>
                  {hasOffensiveWords && <OffensiveContentWarning detectedWords={detectedWords} />}
                </AnimatePresence>
              </motion.div>

              {/* Submit Button */}
              <motion.div 
                className="flex gap-3 pt-4"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
              >
                <Button 
                  variant="outline" 
                  onClick={onClose}
                  disabled={isSubmitting}
                  className="flex-1"
                >
                  Cancelar
                </Button>
                
                <Button 
                  variant="primary"
                  onClick={handleSubmit} 
                  disabled={isSubmitDisabled}
                  className="flex-1"
                >
                  {isSubmitting ? (
                    <div className="flex items-center gap-2">
                      <motion.div
                        className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full"
                        animate={{ rotate: 360 }}
                        transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                      />
                      Enviando...
                    </div>
                  ) : (
                    <div className="flex items-center gap-2">
                      <Sparkles className="w-4 h-4" />
                      {existingReview ? 'Actualizar' : 'Enviar calificación'}
                    </div>
                  )}
                </Button>
              </motion.div>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </AnimatePresence>
  );
};

export default RatingModal;